import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import Icon, {ICON_TYPE} from "@/components/library/Icon";
import {Card, CardContent} from "@/components/ui/card";

export enum GetStartedCardType {
   OrangePink = "orange-pink",
   BlueGreen = "blue-green",
   PinkOrange = "pink-orange",
}

type GetStartedBackgroundImageUrl = {
   background: {
      type: "url";
      backgroundImage: string;
   };
};

type GetStartedBackgroundImageType = {
   background: {
      type: "type";
      backgroundType: GetStartedCardType;
   };
};

type GetStartedCardBaseProps = {
   title: string;
   description: string;
   className?: string;
} & (GetStartedBackgroundImageUrl | GetStartedBackgroundImageType);

type GetStartedCardWithHref = GetStartedCardBaseProps & {
   href: string;
   onClick?: never;
};

type GetStartedCardWithOnClick = GetStartedCardBaseProps & {
   href?: never;
   onClick: () => void;
};

type GetStartedCardProps = GetStartedCardWithHref | GetStartedCardWithOnClick;

export default function GetStartedCard({
   title,
   description,
   background,
   className,
   href,
   onClick,
}: Readonly<GetStartedCardProps>) {
   const backgroundImage =
      background.type === "url"
         ? background.backgroundImage
         : `/images/workflow/${background.backgroundType}.png`;

   const containerClasses = clsx(
      "relative",
      "rounded-2xl",
      "overflow-hidden",
      "hover:scale-[1.02]",
      "transition-transform",
      "cursor-pointer",
      "h-full",
      "w-full",
      "text-left",
      "border-0",
      "bg-transparent",
      "p-0",
      className
   );

   const innerContent = (
      <>
         {/* Background gradient image with blur */}
         <div className="absolute inset-0">
            <Image
               src={backgroundImage}
               alt=""
               fill
               className={clsx("object-cover", "blur-sm", "scale-110", "dark:opacity-70")}
            />
         </div>

         {/* Overlay */}
         <div className={clsx("absolute", "inset-0", "bg-white/40", "dark:bg-background/40")} />

         {/* Content card */}
         <div className="relative">
            <Card className="bg-white/80 dark:bg-gray-900/90 m-6 border-0 rounded-xl">
               <CardContent className="flex flex-col p-4 py-6">
                  <div className="flex items-center gap-3">
                     <h3 className="font-normal text-900 text-2xl tracking-wide">{title}</h3>
                     <Icon icon="arrow-right" className="text-900" type={ICON_TYPE.SOLID} />
                  </div>
                  <p className="text-600 text-sm">{description}</p>
               </CardContent>
            </Card>
         </div>
      </>
   );

   if (onClick) {
      return (
         <button type="button" className={containerClasses} onClick={onClick}>
            {innerContent}
         </button>
      );
   }

   if (href) {
      return (
         <Link href={href} className={clsx(containerClasses, "block")}>
            {innerContent}
         </Link>
      );
   }

   return <div className={containerClasses}>{innerContent}</div>;
}
